import React from "react";

import { Divider, SelectChangeEvent, Stack } from "@mui/material";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import { SettingsModuleProps } from "@app/analytics/account/types";
import WorkspaceSettingsAccordion from "@app/analytics/account/WorkspaceSettings/components/WorkspaceSettingsAccordion";
import Dropdown from "@app/common/components/Dropdown";
import DropdownMenuItem from "@app/common/components/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@app/common/components/Dropdown/utils";
import { TOOLTIPS } from "@app/common/content";
import { EnterpriseRole } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceAdmin } from "@app/common/workspaces/workspaceUtils";

import GlobalReportAccessModule from "./GlobalReportAccessModule";
import { MEMBERSHIP_FIELDS } from "../constants";

const AdvancedPermissionsModule = ({
  settings,
  onSettingUpdate,
  workspaceIsInactive,
}: SettingsModuleProps) => {
  const handleInvitationRoleChange = (event: SelectChangeEvent) =>
    onSettingUpdate({
      workspaceInvitations: event.target.value as EnterpriseRole,
    });

  const handlePermissionResolutionChange = (event: SelectChangeEvent) =>
    onSettingUpdate({
      useLeastPermissivePermissionResolution: event.target.value === "true",
    });

  const { workspaceUser } = useWorkspace();

  const canAccessGlobalReportSetting = isWorkspaceAdmin(workspaceUser);

  return (
    <WorkspaceSettingsAccordion
      title="Advanced Permissions"
      subtitle="Set membership rules and advanced viewership controls"
    >
      <Stack spacing={1.5}>
        <SettingsTitle
          title={MEMBERSHIP_FIELDS.INVITATIONS.title}
          subtitle={MEMBERSHIP_FIELDS.INVITATIONS.subtitle}
        />
        <Dropdown
          disabled={workspaceIsInactive}
          selectedItem={getSelectedFromOptions(
            MEMBERSHIP_FIELDS.INVITATIONS.options,
            settings.workspaceInvitations
          )}
          name={MEMBERSHIP_FIELDS.INVITATIONS.name}
          onChange={handleInvitationRoleChange}
          sx={{ width: "600px", maxWidth: "100%" }}
          tooltip={
            workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
          }
        >
          {MEMBERSHIP_FIELDS.INVITATIONS.options.map((item) => {
            const isSelectedItem = item.value === settings.workspaceInvitations;
            return (
              <DropdownMenuItem
                key={item.value}
                {...item}
                selected={isSelectedItem}
              />
            );
          })}
        </Dropdown>
      </Stack>

      <Divider />
      <Stack spacing={1.5}>
        <SettingsTitle
          title={MEMBERSHIP_FIELDS.TEAM_PERMISSION_RESOLUTION.title}
          subtitle={MEMBERSHIP_FIELDS.TEAM_PERMISSION_RESOLUTION.subtitle}
        />
        <Dropdown
          disabled={workspaceIsInactive}
          selectedItem={getSelectedFromOptions(
            MEMBERSHIP_FIELDS.TEAM_PERMISSION_RESOLUTION.options,
            `${settings.useLeastPermissivePermissionResolution}`
          )}
          name={MEMBERSHIP_FIELDS.TEAM_PERMISSION_RESOLUTION.name}
          onChange={handlePermissionResolutionChange}
          sx={{ width: "600px", maxWidth: "100%" }}
          tooltip={
            workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
          }
        >
          {MEMBERSHIP_FIELDS.TEAM_PERMISSION_RESOLUTION.options.map((item) => {
            const isSelectedItem =
              item.value ===
              `${settings.useLeastPermissivePermissionResolution}`;
            return (
              <DropdownMenuItem
                key={item.value}
                {...item}
                selected={isSelectedItem}
              />
            );
          })}
        </Dropdown>
      </Stack>
      <Divider />
      {canAccessGlobalReportSetting && <GlobalReportAccessModule />}
    </WorkspaceSettingsAccordion>
  );
};

export default AdvancedPermissionsModule;
